import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ItemInterest {
  id: string;
  item_id: string;
  sender_id: string;
  offer_cents: number | null;
  message: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'countered';
  created_at: string;
  responded_at: string | null;
  counter_offer_cents: number | null;
  counter_message: string | null;
  countered_at: string | null;
  wants_trade: boolean;
}

interface CreateInterestInput {
  item_id: string;
  offer_cents?: number;
  message?: string;
  wants_trade?: boolean;
}

// Create an offer/interest on an item
export function useCreateInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateInterestInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('item_interests')
        .insert({
          item_id: input.item_id,
          sender_id: user.id,
          offer_cents: input.offer_cents || null,
          message: input.message || null,
          wants_trade: input.wants_trade ?? false,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('You have already made an offer on this item');
        }
        throw error;
      }

      // Send notification (fire and forget)
      supabase.functions.invoke('send-offer-notification', {
        body: { interest_id: data.id, type: 'new_offer' }
      }).catch(err => console.error('Failed to send notification:', err));

      return data as ItemInterest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['item-interests', variables.item_id] });
      queryClient.invalidateQueries({ queryKey: ['my-sent-interests'] });
    },
  });
}

// Get interests on a specific item (for item owner)
export function useItemInterests(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-interests', itemId],
    queryFn: async () => {
      if (!itemId) return [];

      const { data, error } = await supabase
        .from('item_interests')
        .select(`
          *,
          sender:profiles!item_interests_sender_id_fkey(
            user_id,
            username,
            avatar_url
          )
        `)
        .eq('item_id', itemId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as (ItemInterest & { sender: any })[];
    },
    enabled: !!itemId,
  });
}

// Get all interests received on my items (for sellers - inbox)
export function useReceivedInterests() {
  return useQuery({
    queryKey: ['received-interests'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // First get all my items
      const { data: myItems, error: itemsError } = await supabase
        .from('user_items')
        .select('id')
        .eq('user_id', user.id);

      if (itemsError) throw itemsError;
      if (!myItems || myItems.length === 0) return [];

      const itemIds = myItems.map(item => item.id);

      // Then get all interests on those items
      const { data, error } = await supabase
        .from('item_interests')
        .select(`
          *,
          sender:profiles!item_interests_sender_id_fkey(
            user_id,
            username,
            avatar_url,
            city,
            state_code
          ),
          item:user_items(
            id,
            title,
            asking_price_cents,
            visibility,
            images:user_item_images(image_url, display_order)
          )
        `)
        .in('item_id', itemIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as (ItemInterest & { sender: any; item: any })[];
    },
  });
}

// Get my sent interests (for buyers)
export function useMySentInterests() {
  return useQuery({
    queryKey: ['my-sent-interests'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('item_interests')
        .select(`
          *,
          item:user_items(
            id,
            title,
            asking_price_cents,
            visibility,
            images:user_item_images(image_url, display_order),
            seller:profiles!user_items_user_id_fkey(
              user_id,
              username,
              avatar_url
            )
          )
        `)
        .eq('sender_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as (ItemInterest & { item: any })[];
    },
  });
}

// Check if current user has already sent interest on an item
export function useMyInterestOnItem(itemId: string | undefined) {
  return useQuery({
    queryKey: ['my-interest', itemId],
    queryFn: async () => {
      if (!itemId) return null;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('item_interests')
        .select('*')
        .eq('item_id', itemId)
        .eq('sender_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as ItemInterest | null;
    },
    enabled: !!itemId,
  });
}

// Respond to an interest (accept/decline)
export function useRespondToInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      interestId, 
      status 
    }: { 
      interestId: string; 
      status: 'accepted' | 'declined';
    }) => {
      const { data, error } = await supabase
        .from('item_interests')
        .update({ 
          status, 
          responded_at: new Date().toISOString() 
        })
        .eq('id', interestId)
        .select()
        .single();

      if (error) throw error;

      // Send notification (fire and forget)
      const notificationType = status === 'accepted' ? 'offer_accepted' : 'offer_declined';
      supabase.functions.invoke('send-offer-notification', {
        body: { interest_id: interestId, type: notificationType }
      }).catch(err => console.error('Failed to send notification:', err));

      return data as ItemInterest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-interests'] });
      queryClient.invalidateQueries({ queryKey: ['received-interests'] });
    },
  });
}

// Delete my sent interest
export function useDeleteInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (interestId: string) => {
      const { error } = await supabase
        .from('item_interests')
        .delete()
        .eq('id', interestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-sent-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-interest'] });
    },
  });
}

// Get count of pending received offers (for inbox badge)
export function useUnreadOffersCount() {
  return useQuery({
    queryKey: ['unread-offers-count'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Get my item IDs
      const { data: myItems, error: itemsError } = await supabase
        .from('user_items')
        .select('id')
        .eq('user_id', user.id);

      if (itemsError || !myItems || myItems.length === 0) return 0;

      const itemIds = myItems.map(item => item.id);

      // Count pending interests on my items
      const { count, error } = await supabase
        .from('item_interests')
        .select('*', { count: 'exact', head: true })
        .in('item_id', itemIds)
        .eq('status', 'pending');

      if (error) return 0;
      return count ?? 0;
    },
  });
}

// Counter-offer on an interest (item owner responds with counter)
export function useCounterOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      interestId,
      counterOfferCents,
      counterMessage,
    }: {
      interestId: string;
      counterOfferCents: number;
      counterMessage?: string;
    }) => {
      const { data, error } = await supabase
        .from('item_interests')
        .update({
          status: 'countered',
          counter_offer_cents: counterOfferCents,
          counter_message: counterMessage || null,
          countered_at: new Date().toISOString(),
        })
        .eq('id', interestId)
        .select()
        .single();

      if (error) throw error;

      // Send notification (fire and forget)
      supabase.functions.invoke('send-offer-notification', {
        body: { interest_id: interestId, type: 'counter_offer' }
      }).catch(err => console.error('Failed to send notification:', err));

      return data as ItemInterest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-interests'] });
      queryClient.invalidateQueries({ queryKey: ['received-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-sent-interests'] });
    },
  });
}

// Accept a counter-offer (buyer accepts seller's counter)
export function useAcceptCounterOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (interestId: string) => {
      // First get the counter offer value
      const { data: interest, error: fetchError } = await supabase
        .from('item_interests')
        .select('counter_offer_cents')
        .eq('id', interestId)
        .single();

      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('item_interests')
        .update({
          status: 'accepted',
          offer_cents: interest.counter_offer_cents,
          responded_at: new Date().toISOString(),
        })
        .eq('id', interestId)
        .select()
        .single();

      if (error) throw error;

      // Send notification
      supabase.functions.invoke('send-offer-notification', {
        body: { interest_id: interestId, type: 'counter_accepted' }
      }).catch(err => console.error('Failed to send notification:', err));

      return data as ItemInterest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-interests'] });
      queryClient.invalidateQueries({ queryKey: ['received-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-sent-interests'] });
    },
  });
}
